// frontend/src/components/UserDropdown/UserDropdown.jsx

import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Dropdown, Space, Typography, Tooltip, Drawer, List } from 'antd';
import { SettingOutlined, DownOutlined, TagsOutlined, LogoutOutlined, CrownOutlined, CustomerServiceOutlined, RocketOutlined } from '@ant-design/icons';
import { getSuporteMailto } from '../../constants/support';
import { restorePrimeirosPassos } from '../../utils/primeirosPassosStorage';
import './UserDropdown.css';

const { Text } = Typography;

const getDisplayName = (userObj) => {
    if (!userObj) return 'Usuário';
    if (userObj.nome_completo && userObj.nome_completo.trim() !== '') {
        return userObj.nome_completo.split(' ')[0] || 'Usuário';
    }
    if (userObj.email && userObj.email.trim() !== '') {
        return userObj.email.split('@')[0];
    }
    return 'Usuário';
};

const UserDropdown = ({ isMobile = false }) => {
    const { user, logout, temModuloFinanceiro } = useAuth();
    const navigate = useNavigate();
    const [sheetOpen, setSheetOpen] = useState(false);

    const handleLogout = () => {
        setSheetOpen(false);
        logout();
        navigate('/login');
    };

    const handlePrimeirosPassos = () => {
        setSheetOpen(false);
        restorePrimeirosPassos(user?.id ?? user?.pk ?? 'anon');
        navigate('/meu-desempenho?primeiros=1');
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const sheetItems = [
        {
            key: '1',
            icon: <SettingOutlined />,
            label: 'Configurações Gerais',
            onClick: () => {
                setSheetOpen(false);
                navigate('/configuracoes');
            },
        },
        ...(temModuloFinanceiro
            ? [{
                key: '2',
                icon: <TagsOutlined />,
                label: 'Gerenciar Categorias',
                onClick: () => {
                    setSheetOpen(false);
                    navigate('/categorias');
                },
            }]
            : []),
        {
            key: 'primeiros-passos',
            icon: <RocketOutlined />,
            label: 'Primeiros passos',
            onClick: handlePrimeirosPassos,
        },
        {
            key: 'suporte',
            icon: <CustomerServiceOutlined />,
            label: 'Suporte',
            href: getSuporteMailto({ emailConta: user?.email }),
        },
        {
            key: '3',
            icon: <LogoutOutlined />,
            label: 'Sair',
            danger: true,
            onClick: handleLogout,
        },
    ];

    const menuItems = [
        {
            type: 'group',
            label: <Text strong>{user?.email || 'Usuário'}</Text>,
        },
        {
            key: '1',
            icon: <SettingOutlined />,
            label: <Link to="/configuracoes">Configurações Gerais</Link>,
        },
        ...(temModuloFinanceiro
            ? [{
                key: '2',
                icon: <TagsOutlined />,
                label: <Link to="/categorias">Gerenciar Categorias</Link>,
            }]
            : []),
        {
            key: 'primeiros-passos',
            icon: <RocketOutlined />,
            label: 'Primeiros passos',
            onClick: handlePrimeirosPassos,
        },
        {
            key: 'suporte',
            icon: <CustomerServiceOutlined />,
            label: (
                <a href={getSuporteMailto({ emailConta: user?.email })}>
                    Suporte
                </a>
            ),
        },
        {
            type: 'divider',
        },
        {
            key: '3',
            icon: <LogoutOutlined />,
            label: 'Sair',
            onClick: handleLogout,
            danger: true,
        }
    ];

    const trigger = (
        <a
            onClick={(e) => {
                e.preventDefault();
                if (isMobile) setSheetOpen(true);
            }}
            className={isMobile ? 'user-menu-trigger user-menu-trigger--mobile' : 'user-menu-trigger'}
            aria-label="Menu da conta"
        >
            <span className="user-menu-trigger__name">{getDisplayName(user)}</span>
            <DownOutlined className="user-menu-trigger__caret" />
        </a>
    );

    return (
        <Space align="center" size="small">
            {user?.acesso_vitalicio && (
                <Tooltip title="Acesso vitalício">
                    <span
                        className="acesso-vitalicio-badge"
                        aria-label="Acesso vitalício"
                    >
                        <CrownOutlined />
                    </span>
                </Tooltip>
            )}
            {isMobile ? (
                <>
                    {trigger}
                    <Drawer
                        open={sheetOpen}
                        onClose={() => setSheetOpen(false)}
                        placement="bottom"
                        height="auto"
                        title={user?.email || 'Conta'}
                        styles={{
                            body: { paddingBottom: 'calc(16px + env(safe-area-inset-bottom, 0px))' },
                            content: { borderRadius: '16px 16px 0 0' },
                        }}
                    >
                        <List
                            dataSource={sheetItems}
                            split={false}
                            renderItem={(item) => (
                                <List.Item
                                    style={{
                                        cursor: 'pointer',
                                        padding: '14px 4px',
                                        color: item.danger ? 'var(--color-danger, #D64545)' : undefined,
                                    }}
                                    onClick={() => {
                                        if (item.href) {
                                            window.location.href = item.href;
                                            return;
                                        }
                                        item.onClick?.();
                                    }}
                                >
                                    <List.Item.Meta
                                        avatar={item.icon}
                                        title={item.label}
                                    />
                                </List.Item>
                            )}
                        />
                    </Drawer>
                </>
            ) : (
                <Dropdown menu={{ items: menuItems }} trigger={['click']}>
                    {trigger}
                </Dropdown>
            )}
        </Space>
    );
};

export default UserDropdown;
